import { createOrderIndexAndFixup } from '@gitbook/util-orderindex';

import {
    generateTagSlug,
    getTagGroupChildren,
    isTagGroupDescendant,
    normalizeTagLabel,
} from '../../tags';
import type { Revision, RevisionTags, RevisionTagGroups } from '../../types';
import { addUserEdits, createEdits, mutations, yToJSON } from '../../utils';

export type TagGroupPlacement = { into?: string | null; at?: number };

/** Insert a group using the same validation for realtime and API edits. */
export function insertTagGroupInRevision(
    revision: mutations.MutableData<Revision>,
    input: { slug?: string; label: string } & TagGroupPlacement,
    authors: string[] = []
): void {
    const label = normalizeTagLabel(input.label);
    const slug = input.slug ?? generateTagSlug(label);
    if (!label || !slug || generateTagSlug(slug) !== slug)
        throw new Error('Invalid tag group name or slug');
    const groups = getGroups(revision);
    if (Object.hasOwn(groups, slug)) throw new Error('Tag group with same slug already exists');
    const parent = input.into ?? undefined;
    validatePlacement(groups, slug, parent, input.at);
    let target = mutations.get(revision, 'tagGroups');
    if (!target) {
        mutations.set(revision, 'tagGroups', {});
        target = mutations.get(revision, 'tagGroups')!;
    }
    const orderIndex = createGroupOrderIndex(target, groups, slug, parent, input.at, authors);
    mutations.set(target, slug, { slug, label, parent, orderIndex, edits: createEdits(authors) });
}

export function updateTagGroupInRevision(
    revision: mutations.MutableData<Revision>,
    slug: string,
    update: { label?: string } & TagGroupPlacement,
    authors: string[] = []
): void {
    const groups = getGroups(revision);
    const existing = groups[slug];
    if (!Object.hasOwn(groups, slug)) throw new Error('Tag group does not exist');
    const parent = update.into === undefined ? existing.parent : (update.into ?? undefined);
    validatePlacement(groups, slug, parent, update.at);
    const label = update.label === undefined ? existing.label : normalizeTagLabel(update.label);
    if (!label) throw new Error('Invalid tag group name');
    const target = mutations.get(revision, 'tagGroups')!;
    const group = mutations.get(target, slug);
    if (parent !== existing.parent || update.at !== undefined) {
        mutations.set(
            group,
            'orderIndex',
            createGroupOrderIndex(target, groups, slug, parent, update.at, authors)
        );
    }
    if (parent === undefined) mutations.del(group, 'parent');
    else mutations.set(group, 'parent', parent);
    mutations.set(group, 'label', label);
    if (authors.length)
        mutations.set(group, 'edits', addUserEdits(mutations.get(group, 'edits'), authors));
}

export function removeTagGroupInRevision(
    revision: mutations.MutableData<Revision>,
    slug: string
): void {
    const groups = getGroups(revision);
    if (!Object.hasOwn(groups, slug)) throw new Error('Tag group does not exist');
    const tags: RevisionTags = yToJSON(mutations.get(revision, 'tags')) ?? {};
    if (
        Object.values(groups).some((group) => group.parent === slug) ||
        Object.values(tags).some((tag) => tag.group === slug)
    ) {
        throw new Error('Move all tags and subgroups before deleting this group');
    }
    mutations.del(mutations.get(revision, 'tagGroups')!, slug);
}

export function setTagGroupInRevision(
    revision: mutations.MutableData<Revision>,
    slug: string,
    group: string | null,
    authors: string[] = []
): void {
    const tags = mutations.get(revision, 'tags');
    const tag = tags && mutations.get(tags, slug);
    if (!tag || !Object.hasOwn(yToJSON(tags), slug)) throw new Error('Tag does not exist');
    if (group !== null && !Object.hasOwn(getGroups(revision), group))
        throw new Error('Tag group does not exist');
    if (group === null) mutations.del(tag, 'group');
    else mutations.set(tag, 'group', group);
    if (authors.length)
        mutations.set(tag, 'edits', addUserEdits(mutations.get(tag, 'edits'), authors));
}

/** Repair references after concurrent edits or merges without discarding content. */
export function normalizeTagGroupsInRevision(revision: mutations.MutableData<Revision>): boolean {
    const groups = getGroups(revision);
    const target = mutations.get(revision, 'tagGroups');
    let changed = false;
    for (const slug of Object.keys(groups).sort()) {
        const group = groups[slug];
        if (
            group.parent &&
            (!Object.hasOwn(groups, group.parent) ||
                isTagGroupDescendant(groups, slug, group.parent))
        ) {
            delete group.parent;
            mutations.del(mutations.get(target!, slug), 'parent');
            changed = true;
        }
    }
    const tags = mutations.get(revision, 'tags');
    if (tags)
        for (const tag of mutations.values(tags)) {
            const group = mutations.get(tag, 'group');
            if (group && !Object.hasOwn(groups, group)) {
                mutations.del(tag, 'group');
                changed = true;
            }
        }
    return changed;
}

function getGroups(revision: mutations.MutableData<Revision>): RevisionTagGroups {
    return yToJSON(mutations.get(revision, 'tagGroups')) ?? {};
}

function validatePlacement(
    groups: RevisionTagGroups,
    slug: string,
    parent: string | undefined,
    at: number | undefined
) {
    if (parent && !Object.hasOwn(groups, parent))
        throw new Error('Parent tag group does not exist');
    if (parent && isTagGroupDescendant(groups, slug, parent))
        throw new Error('Tag groups cannot contain a cycle');
    if (at !== undefined && (!Number.isInteger(at) || at < 0))
        throw new Error('Invalid tag group position');
}

function createGroupOrderIndex(
    target: mutations.MutableData<RevisionTagGroups>,
    groups: RevisionTagGroups,
    slug: string,
    parent?: string,
    at?: number,
    authors: string[] = []
) {
    const siblings = getTagGroupChildren(groups, parent).filter((group) => group.slug !== slug);
    const index = Math.min(at ?? siblings.length, siblings.length);
    const { index: orderIndex, fixAfterIndex } = createOrderIndexAndFixup({
        before: siblings[index - 1]?.orderIndex,
        after: siblings[index]?.orderIndex,
        nextSiblingIndex: siblings[index + 1]?.orderIndex,
    });
    if (fixAfterIndex) {
        const sibling = mutations.get(target, siblings[index].slug);
        mutations.set(sibling, 'orderIndex', fixAfterIndex);
        if (authors.length)
            mutations.set(sibling, 'edits', addUserEdits(mutations.get(sibling, 'edits'), authors));
    }
    return orderIndex;
}
